import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const D2C_PENDING_DIRECT_CHECKOUT_KEY = 'd2c-pending-direct-checkout';

/**
 * Synchronous: marks an in-flight D2C direct-checkout add so the cart drawer can skip auto-open
 * (same-tab race before redirect or if detection flags disagree).
 */
function d2cSetPendingDirectCheckout() {
  try {
    sessionStorage.setItem(D2C_PENDING_DIRECT_CHECKOUT_KEY, '1');
  } catch (_) {}
}

function d2cClearPendingDirectCheckout() {
  try {
    sessionStorage.removeItem(D2C_PENDING_DIRECT_CHECKOUT_KEY);
  } catch (_) {}
}

/** Marks add-to-cart buttons we disabled for an in-flight request (so we do not re-enable sold-out). */
const ATC_REQUEST_BUSY = 'data-atc-request-busy';
/** On `.shopify-section` for CSS (pointer-events) — includes sticky CTA, works without :has() */
const ATC_SECTION_PENDING_CLASS = 'atc-add-request-pending';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** Observes PDP section DOM (e.g. Skio) until frequency select exists and syncs to hidden selling_plan */
  /** @type {MutationObserver | null} */
  #skioSellingPlanObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    /** @param {Event} e */
    const onSkioOrFrequencyChange = (e) => {
      const t = e.target;
      if (!(t instanceof HTMLSelectElement)) return;
      if (!t.hasAttribute('skio-selling-plans') && !t.classList.contains('skio-frequency')) return;
      this.#applySkioSelectToSellingPlanInput(t);
    };
    target?.addEventListener('change', onSkioOrFrequencyChange, { signal });

    if (target && this.querySelector('form input[name="selling_plan"]')) {
      this.#syncSkioSellingPlanToHiddenInput();
      const sectionEl = /** @type {HTMLElement | null} */ (this.closest('.shopify-section'));
      if (sectionEl) {
        this.#skioSellingPlanObserver = new MutationObserver(() => this.#syncSkioSellingPlanToHiddenInput());
        this.#skioSellingPlanObserver.observe(sectionEl, { childList: true, subtree: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#skioSellingPlanObserver?.disconnect();
    this.#skioSellingPlanObserver = null;
    this.#abortController.abort();
  }

  /**
   * PDP: Skio injects `<select skio-selling-plans>` / `.skio-frequency`; keep Buy Buttons hidden input in sync.
   */
  #syncSkioSellingPlanToHiddenInput() {
    const section = this.closest('.shopify-section');
    const form = this.querySelector('form');
    const hidden = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="selling_plan"]'));
    if (!section || !hidden) return;
    const skioSel = /** @type {HTMLSelectElement | null} */ (
      section.querySelector('select[skio-selling-plans], select.skio-frequency')
    );
    if (skioSel?.value) hidden.value = skioSel.value;
  }

  /**
   * @param {HTMLSelectElement} selectEl - Skio widget frequency dropdown
   */
  #applySkioSelectToSellingPlanInput(selectEl) {
    const form = this.querySelector('form');
    const hidden = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="selling_plan"]'));
    if (!hidden) return;
    if (selectEl.value) hidden.value = selectEl.value;
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * D2C buy buttons: direct checkout (theme editor). Uses data attr + wrapper class so intent
   * still applies if the custom element is re-rendered without attributes.
   * @returns {boolean}
   */
  #wantsD2cDirectCheckout() {
    if (this.dataset.d2cDirectCheckout === 'true') return true;
    return Boolean(this.closest('.d2c-buy-buttons--direct-checkout'));
  }

  /**
   * Disables primary and sticky add-to-cart buttons until the network + apply flow finishes.
   */
  #lockAddToCartButtons() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.classList.add(ATC_SECTION_PENDING_CLASS);
    }
    const buttons = section?.querySelectorAll(
      'add-to-cart-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]'
    );
    if (!buttons) return;
    for (const btn of buttons) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      if (btn.disabled) continue;
      btn.setAttribute(ATC_REQUEST_BUSY, '');
      btn.disabled = true;
    }
  }

  /**
   * Re-enables only the buttons locked by {@link #lockAddToCartButtons}.
   */
  #unlockAddToCartButtons() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.classList.remove(ATC_SECTION_PENDING_CLASS);
    }
    const buttons = section?.querySelectorAll(
      'add-to-cart-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]'
    );
    if (!buttons) return;
    for (const btn of buttons) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      if (!btn.hasAttribute(ATC_REQUEST_BUSY)) continue;
      btn.removeAttribute(ATC_REQUEST_BUSY);
      btn.disabled = false;
    }
  }

  /**
   * Clears the cart so direct checkout only contains the new add (and D2C free gifts).
   * @returns {Promise<void>}
   */
  async #clearCartForDirectCheckout() {
    const clearUrl = Theme.routes?.cart_clear_url;
    if (!clearUrl) return;
    try {
      await fetch(clearUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: '{}',
      });
    } catch (error) {
      console.error('Failed to clear cart before direct checkout:', error);
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * D2C free gift block: variant ids to add with the main line (see `d2c-free-gifr` script tag).
   * @returns {{ parentProductId: number, lines: { variantId: number }[] } | null}
   */
  #getD2cFreeGiftConfig() {
    const section = this.closest('.shopify-section');
    if (!section) return null;
    const el = section.querySelector('script.d2c-free-gift-line-items[type="application/json"]');
    if (!el?.textContent?.trim()) return null;
    try {
      const data = JSON.parse(el.textContent.trim());
      if (!Array.isArray(data.lines) || data.lines.length === 0) return null;
      return data;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} mainVariantId
   * @param {{ lines: { variantId: number }[] }} config
   * @returns {Array<{ id: number, quantity: number, properties: Record<string, string> }>}
   */
  #buildD2cFreeGiftLineItems(mainVariantId, config) {
    return config.lines.map((l) => ({
      id: Number(l.variantId),
      quantity: 1,
      properties: {
        _d2c_free_gift: '1',
        _d2c_parent_variant: String(mainVariantId),
      },
    }));
  }

  /**
   * @param {*} cartLine
   * @param {string} key
   * @returns {string | null | undefined}
   */
  #d2cCartItemProp(cartLine, key) {
    const p = cartLine?.properties;
    if (p == null) return undefined;
    if (typeof p === 'object' && !Array.isArray(p)) {
      return /** @type {Record<string, string>} */ (p)[key];
    }
    return undefined;
  }

  /**
   * @returns {Promise<Cart | null>}
   */
  async #fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  /**
   * Omits free-gift lines whose variant is already in the cart for this parent (same D2C gift + parent).
   * @param {string} mainVariantIdStr
   * @param {{ lines: { variantId: number }[] } | null} giftConfig
   * @returns {Promise<Array<{ id: number, quantity: number, properties: Record<string, string> }>>}
   */
  async #getFilteredD2cFreeGiftLineItems(mainVariantIdStr, giftConfig) {
    if (!giftConfig || !mainVariantIdStr) return [];
    const built = this.#buildD2cFreeGiftLineItems(mainVariantIdStr, giftConfig);
    if (built.length === 0) return [];
    const cart = await this.#fetchCartData();
    if (!cart?.items?.length) return built;
    return built.filter((line) => {
      const already = cart.items.some((row) => {
        if (Number(row.variant_id) !== line.id) return false;
        const g = this.#d2cCartItemProp(row, '_d2c_free_gift');
        const parent = this.#d2cCartItemProp(row, '_d2c_parent_variant');
        return String(g) === '1' && String(parent) === String(mainVariantIdStr);
      });
      return !already;
    });
  }

  /**
   * @param {*} response
   * @param {object} ctx
   * @param {HTMLFormElement} ctx.form
   * @param {FormData} ctx.formData
   * @param {NodeListOf<AddToCartComponent>} ctx.allAddToCartContainers
   * @param {HTMLElement | undefined} ctx.addToCartTextError
   * @param {Event} [ctx.event]
   * @param {number} [ctx.itemCountForBubble]
   * @param {boolean} [ctx.directCheckout]
   */
  #applyProductFormCartAddResponse(response, ctx) {
    const { form, formData, allAddToCartContainers, addToCartTextError, event, itemCountForBubble, directCheckout } = ctx;
    const baseQty = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const successItemCount = itemCountForBubble !== undefined ? itemCountForBubble : baseQty;

    if (response.status) {
      d2cClearPendingDirectCheckout();
      this.dispatchEvent(
        new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
      );

      if (!addToCartTextError) return;
      addToCartTextError.classList.remove('hidden');

      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = response.message;
      } else {
        const newTextNode = document.createTextNode(response.message);
        addToCartTextError.appendChild(newTextNode);
      }

      this.#setLiveRegionText(response.message);

      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          didError: true,
          source: 'product-form-component',
          itemCount: baseQty,
          productId: this.dataset.productId,
        })
      );
      return;
    }

    const id = formData.get('id');
    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }

    if (!id) throw new Error('Form ID is required');

    let d2cPending = false;
    try {
      d2cPending = sessionStorage.getItem(D2C_PENDING_DIRECT_CHECKOUT_KEY) === '1';
    } catch (_) {}
    const goDirectCheckout = d2cPending || directCheckout || this.#wantsD2cDirectCheckout();
    if (goDirectCheckout) {
      d2cClearPendingDirectCheckout();
      const raw = Theme.routes?.checkout_url && String(Theme.routes.checkout_url).trim();
      const target = raw || '/checkout';
      window.location.assign(
        target.startsWith('http') || target.startsWith('//') ? target : new URL(target, window.location.origin).href
      );
      return;
    }

    d2cClearPendingDirectCheckout();

    const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
    if (anyAddToCartButton) {
      const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
      this.#setLiveRegionText(addedText);
      setTimeout(() => {
        this.#clearLiveRegionText();
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    }

    // Open drawer and cart UI on CartAddEvent immediately. Do not wait for GET /cart.js — that
    // extra round-trip delayed the drawer. PDP quantity still syncs via #fetchAndUpdateCartQuantity.
    this.dispatchEvent(
      new CartAddEvent(response, id.toString(), {
        source: 'product-form-component',
        itemCount: successItemCount,
        productId: this.dataset.productId,
        sections: response.sections,
      })
    );
    void this.#fetchAndUpdateCartQuantity();
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      const section = this.closest('.shopify-section');
      const stickyAtc = section?.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
      const stickyDisabled = stickyAtc instanceof HTMLButtonElement && stickyAtc.disabled;
      if (anyButtonDisabled || stickyDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = /** @type {string[]} */ ([]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const mainVariantIdStr = String(formData.get('id') || '');
    const mainQty = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const giftConfig = this.#getD2cFreeGiftConfig();

    const afterAdd = () => {
      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    };

    const directCheckout = this.#wantsD2cDirectCheckout();
    if (directCheckout) {
      d2cSetPendingDirectCheckout();
    } else {
      d2cClearPendingDirectCheckout();
    }

    this.#lockAddToCartButtons();

    void (async () => {
      let giftLineItems;
      try {
        if (directCheckout) {
          await this.#clearCartForDirectCheckout();
        }
        giftLineItems = await this.#getFilteredD2cFreeGiftLineItems(mainVariantIdStr, giftConfig);
      } catch (err) {
        d2cClearPendingDirectCheckout();
        this.#unlockAddToCartButtons();
        afterAdd();
        console.error(err);
        return;
      }

      /** @param {any} response */
      const applyResponse = (response) => {
        const baseQty = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
        const itemCountForBubble =
          giftLineItems.length > 0 ? baseQty + giftLineItems.length : undefined;
        this.#applyProductFormCartAddResponse(response, {
          form,
          formData,
          allAddToCartContainers,
          addToCartTextError,
          event,
          itemCountForBubble,
          directCheckout,
        });
      };

      if (giftLineItems.length > 0) {
        /** @type {{ id: number, quantity: number, selling_plan?: number }} */
        const mainLine = {
          id: Number(mainVariantIdStr),
          quantity: mainQty,
        };
        const spEl = form.querySelector('input[name="selling_plan"]');
        if (spEl instanceof HTMLInputElement && spEl.value) {
          mainLine.selling_plan = Number(spEl.value);
        }
        const payload = {
          items: [mainLine, ...giftLineItems],
          sections: cartItemComponentsSectionIds.join(','),
        };
        fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((response) => applyResponse(response))
          .catch((error) => {
            d2cClearPendingDirectCheckout();
            console.error(error);
          })
          .finally(() => {
            this.#unlockAddToCartButtons();
            afterAdd();
          });
        return;
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      })
        .then((response) => response.json())
        .then((response) => applyResponse(response))
        .catch((error) => {
          d2cClearPendingDirectCheckout();
          console.error(error);
        })
        .finally(() => {
          this.#unlockAddToCartButtons();
          afterAdd();
        });
    })();
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const atcForGuard = this.querySelectorAll('add-to-cart-component');
    const anyAtcDisabled = Array.from(atcForGuard).some((c) => {
      return /** @type {AddToCartComponent} */ (c).refs?.addToCartButton?.disabled;
    });
    const sectionForGuard = this.closest('.shopify-section');
    const stickyForGuard = sectionForGuard?.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
    const stickyDisabledGuard = stickyForGuard instanceof HTMLButtonElement && stickyForGuard.disabled;
    if (anyAtcDisabled || stickyDisabledGuard) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const formForBatch = this.querySelector('form');
    const batchSpEl = formForBatch?.querySelector('input[name="selling_plan"]');
    const batchSellingPlan = batchSpEl instanceof HTMLInputElement ? batchSpEl.value : '';
    const giftConfig = this.#getD2cFreeGiftConfig();
    const firstQueued = items[0];
    const directCheckout = this.#wantsD2cDirectCheckout();
    if (directCheckout) {
      d2cSetPendingDirectCheckout();
    } else {
      d2cClearPendingDirectCheckout();
    }

    this.#lockAddToCartButtons();

    void (async () => {
      /** @type {Array<{ id: number, quantity: number, properties: Record<string, string> }>} */
      let extraGifts;
      try {
        if (directCheckout) {
          await this.#clearCartForDirectCheckout();
        }
        extraGifts = [];
        if (giftConfig?.lines?.length && firstQueued) {
          extraGifts = await this.#getFilteredD2cFreeGiftLineItems(String(firstQueued.variantId), giftConfig);
        }
      } catch (err) {
        d2cClearPendingDirectCheckout();
        this.#unlockAddToCartButtons();
        console.error(err);
        return;
      }

      const payload = {
        items: items.map((item) => {
          /** @type {{ id: number, quantity: number, selling_plan?: number }} */
          const line = { id: Number(item.variantId), quantity: item.quantity };
          if (batchSellingPlan) {
            line.selling_plan = Number(batchSellingPlan);
          }
          return line;
        }),
        sections: cartItemComponentsSectionIds.join(','),
      };

      for (const gl of extraGifts) {
        payload.items.push(gl);
      }

      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((response) => {
        if (response.status) {
          d2cClearPendingDirectCheckout();
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        let d2cBatchPending = false;
        try {
          d2cBatchPending = sessionStorage.getItem(D2C_PENDING_DIRECT_CHECKOUT_KEY) === '1';
        } catch (_) {}
        if (d2cBatchPending || directCheckout || this.#wantsD2cDirectCheckout()) {
          d2cClearPendingDirectCheckout();
          const raw = Theme.routes?.checkout_url && String(Theme.routes.checkout_url).trim();
          const target = raw || '/checkout';
          window.location.assign(
            target.startsWith('http') || target.startsWith('//') ? target : new URL(target, window.location.origin).href
          );
          return;
        }

        d2cClearPendingDirectCheckout();

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(response, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
        void this.#fetchAndUpdateCartQuantity();
        })
        .catch((error) => {
          d2cClearPendingDirectCheckout();
          console.error(error);
        })
        .finally(() => {
          this.#unlockAddToCartButtons();
        });
    })();
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    // Subscription products: keep selling_plan in sync with the variant (matches Liquid in buy-buttons on section fetch)
    const formEl = this.querySelector('form');
    const newSellingPlanInput = event.detail.data.html?.querySelector?.('input[name="selling_plan"]');
    const existingSellingPlanInput = formEl?.querySelector('input[name="selling_plan"]');
    if (formEl) {
      if (newSellingPlanInput) {
        if (existingSellingPlanInput instanceof HTMLInputElement) {
          existingSellingPlanInput.value =
            newSellingPlanInput instanceof HTMLInputElement ? newSellingPlanInput.value : '';
        } else {
          formEl.appendChild(/** @type {HTMLInputElement} */ (newSellingPlanInput.cloneNode(true)));
        }
      } else if (existingSellingPlanInput) {
        existingSellingPlanInput.remove();
      }
      this.#syncSkioSellingPlanToHiddenInput();
    }

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
